import L, { DrawEvents, Icon, LatLng, LatLngTuple, Map } from "leaflet";
import {
  MapContainer,
  LayersControl,
  TileLayer,
  FeatureGroup,
  Marker,
  Circle,
  LayerGroup,
} from "react-leaflet";
import { useEffect, useMemo, useRef, useState } from "react";
import "leaflet-extra-markers";
import { EditControl } from "react-leaflet-draw";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerIconRetina from "leaflet/dist/images/marker-icon-2x.png";
import markerIconExtraShadow from "leaflet-extra-markers/dist/img/markers_shadow.png";
import ExplorerModal from "./explorer-modal";
import BoundaryOrPoint from "models/explorer/boundary-or-point";
import { CustomSearch } from "models/explorer/custom-search";
import SearchFeatures from "models/explorer/search-features";
import ExplorerResults from "./explorer-results";
import { Box } from "@mui/material";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";

import "leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css";
import "react-leaflet-markercluster/styles";
import "./index.css";
import "./styles/iconsmind/iconsmind.css";
import "./styles/extramarkers.css";
import AnalyticsService from "services/AnalyticsService";

const analyticsService = new AnalyticsService();

const headerHeight = ["FAF-nt", "nrdata-orig"].includes(
  process.env.REACT_APP_STYLING_THEME ?? "",
)
  ? 80
  : 48;
const position: LatLngTuple = [-27.5, 134];
const iconLeaflet = new Icon({
  iconRetinaUrl: markerIconRetina,
  iconUrl: markerIcon,
  shadowUrl: markerIconExtraShadow,
  iconAnchor: [13, 21],
  iconSize: [25, 43],
});

const Explorer = () => {
  const [map, setMap] = useState<Map | null>(null);
  const [drawControl, setDrawControl] = useState<Function>();
  const [customSearch, setCustomSearch] = useState<CustomSearch>({
    enabled: false,
    radius: 1000,
    type: "none",
  });
  const typeRef = useRef("none");
  const enabledRef = useRef(false);
  const radiusRef = useRef<number>(customSearch.radius);
  const geojsonRef = useRef<{ coordinates: any[]; type: string }>();
  const latlongRef = useRef<LatLng>();
  const [searchFeatures, setSearchFeatures] = useState<SearchFeatures>({
    containedOrIntersecting: "intersecting",
    tags: [],
    systemGuid: undefined,
  });
  //   const [circle, setCircle] = React.useState<JSX.Element>(<></>);
  const [results, setResults] = useState<readonly BoundaryOrPoint[]>([]);
  const googleMarker =
    customSearch.latlong?.lat &&
    customSearch.latlong?.lng &&
    customSearch.type === "google" ? (
      <Marker
        icon={iconLeaflet}
        key={"google-marker"}
        position={customSearch.latlong}
      />
    ) : (
      <></>
    );
  const radiusCircles =
    map &&
    customSearch.latlong?.lat &&
    customSearch.latlong?.lng &&
    customSearch.radius > 0 &&
    (customSearch.type === "google" ||
      customSearch.type === "marker" ||
      customSearch.type === "system-point") ? (
      <Circle
        key={"circle-radius"}
        center={customSearch.latlong}
        radius={customSearch.radius}
      ></Circle>
    ) : (
      <></>
    );

  function setPointAndBoundaries(result: readonly BoundaryOrPoint[]) {
    setResults(result);
  }

  function handleCustomSearch(newCustomSearch: CustomSearch) {
    setCustomSearch(newCustomSearch);
    if (map) {
      if (
        newCustomSearch.latlong &&
        newCustomSearch.radius >= 1 &&
        (newCustomSearch.type === "google" ||
          newCustomSearch.type === "marker" ||
          newCustomSearch.type === "system-point")
      ) {
        try {
          map.fitBounds(
            L.latLng(newCustomSearch.latlong).toBounds(newCustomSearch.radius),
          );
        } catch (error) {
          console.warn("Error on moving zoom to circle");
        }
      } else if (newCustomSearch.geojson) {
        try {
          map.fitBounds(
            L.GeoJSON.coordsToLatLngs(newCustomSearch.geojson?.coordinates, 2),
          );
        } catch (error) {
          console.warn("Error on moving zoom to boundary");
        }
      }
    }
  }

  function _mounted(drawControl: Function) {
    setDrawControl(drawControl);
  }

  const onCreate = (event: DrawEvents.Created) => {
    typeRef.current = event?.layerType;
    let newCustomSearch;
    if (event?.layerType === "marker") {
      enabledRef.current = true;
      latlongRef.current = (event?.layer as any)?._latlng;
      newCustomSearch = {
        ...customSearch,
        enabled: true,
        latlong: (event?.layer as any)?._latlng,
        type: event?.layerType,
      };
    } else if (event?.layerType === "circle") {
      enabledRef.current = true;
      latlongRef.current = (event?.layer as any)?._latlng;
      radiusRef.current = Math.round((event?.layer as any).getRadius());
      newCustomSearch = {
        ...customSearch,
        enabled: true,
        latlong: (event?.layer as any)?._latlng,
        radius: radiusRef.current,
        type: event?.layerType,
      };
      (event?.layer as any)?.bringToBack();
    } else {
      enabledRef.current = true;
      geojsonRef.current = event?.layer?.toGeoJSON()?.geometry;
      newCustomSearch = {
        ...customSearch,
        enabled: true,
        geojson: geojsonRef.current,
        type: event?.layerType,
      };
      (event?.layer as any)?.bringToBack();
    }
    setCustomSearch(newCustomSearch);
  };

  // const onEdit = (event: DrawEvents.Edited) => {
  // };

  const onEditStart = (event: L.DrawEvents.EditStart) => {
    enabledRef.current = false;
    let newCustomSearch;
    if (typeRef.current === "marker") {
      newCustomSearch = {
        ...customSearch,
        enabled: enabledRef.current,
        latlong: latlongRef.current,
        type: typeRef.current,
      };
    } else if (typeRef.current === "circle") {
      newCustomSearch = {
        ...customSearch,
        enabled: enabledRef.current,
        latlong: latlongRef.current,
        radius: radiusRef.current,
        type: typeRef.current,
      };
    } else {
      newCustomSearch = {
        ...customSearch,
        enabled: enabledRef.current,
        geojson: geojsonRef.current,
        type: typeRef.current,
      };
    }
    setCustomSearch(newCustomSearch);
  };

  const onEditMove = (event: DrawEvents.EditMove) => {
    if (typeRef.current === "marker") {
      latlongRef.current = (event?.layer as any)?._latlng;
    } else if (typeRef.current === "circle") {
      latlongRef.current = (event?.layer as any)?._latlng;
      radiusRef.current = (event?.layer as any)?.getRadius();
    } else {
      geojsonRef.current = (event?.layer as any)?.toGeoJSON()?.geometry;
    }
  };

  const onEditStop = (event: DrawEvents.EditStop) => {
    enabledRef.current = true;
    let newCustomSearch;
    if (typeRef.current === "marker") {
      newCustomSearch = {
        ...customSearch,
        enabled: true,
        latlong: latlongRef.current,
        type: typeRef.current,
      };
    } else if (typeRef.current === "circle") {
      newCustomSearch = {
        ...customSearch,
        enabled: true,
        latlong: latlongRef.current,
        radius: radiusRef.current,
        type: typeRef.current,
      };
    } else {
      newCustomSearch = {
        ...customSearch,
        enabled: true,
        geojson: geojsonRef.current,
        type: typeRef.current,
      };
    }
    setCustomSearch(newCustomSearch);
  };

  // const onDrawStart = (event: any) => {
  //   console.log("draw start");
  //   console.log(event);
  // };

  // const onDrawStop = (event: any) => {
  //   console.log("draw stop");
  //   console.log(event);
  // };

  const onEditVertex = (event: DrawEvents.EditVertex) => {
    geojsonRef.current = (event?.poly as any)?.toGeoJSON()?.geometry;
  };

  const onEditResize = (event: DrawEvents.EditResize) => {
    if (typeRef.current === "marker") {
      latlongRef.current = (event?.layer as any)?._latlng;
    } else if (typeRef.current === "circle") {
      latlongRef.current = (event?.layer as any)?._latlng;
      radiusRef.current = Math.round((event?.layer as any)?.getRadius());
    } else {
      geojsonRef.current = (event?.layer as any)?.toGeoJSON()?.geometry;
    }
  };

  const onDelete = (_: L.DrawEvents.Deleted) => {
    typeRef.current = "none";
    setCustomSearch({ ...customSearch, enabled: false, type: typeRef.current });
  };

  const displayMap = useMemo(
    () => (
      <MapContainer
        style={{ height: "100%", width: "100%" }}
        center={position}
        zoom={5}
        zoomAnimation={true}
        ref={setMap}
      >
        <LayersControl position="topleft">
          <LayersControl.BaseLayer checked name="Street">
            <TileLayer
              attribution="Google Maps"
              url="https://{s}.google.com/maps/vt?lyrs=m@189&gl=cn&x={x}&y={y}&z={z}&loading=async"
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Satellite">
            <TileLayer
              attribution="Google Maps"
              url="https://{s}.google.com/maps/vt?lyrs=s@189&gl=cn&x={x}&y={y}&z={z}&loading=async"
              subdomains={["mt0", "mt1", "mt2", "mt3"]}
            />
          </LayersControl.BaseLayer>
          <LayersControl.BaseLayer name="Grayscale">
            <TileLayer
              id="mapbox/light-v9"
              attribution='Map data &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=pk.eyJ1IjoiZGFyaWNjaGVuIiwiYSI6ImNpd2NuazByczA1bTMyeXBvNG1iejByZDYifQ.cQJ6l4b68Y0kXgWB3HnF6w"
            />
          </LayersControl.BaseLayer>
        </LayersControl>
        {map && (
          <LayerGroup>
            {googleMarker}
            {radiusCircles}
          </LayerGroup>
        )}
        {map && results.length >= 1 && (
          <FeatureGroup>
            <ExplorerResults pointsAndBoundaries={results} map={map} />
          </FeatureGroup>
        )}
        <FeatureGroup>
          <EditControl
            position="topleft"
            // onEdited={onEdit}
            // onDrawStart={onDrawStart}
            // onDrawStop={onDrawStop}
            onEditMove={onEditMove}
            onEditStart={onEditStart}
            onEditStop={onEditStop}
            onEditResize={onEditResize}
            onCreated={onCreate}
            onDeleted={onDelete}
            onMounted={_mounted}
            onEditVertex={onEditVertex}
            draw={{
              marker: {
                icon: iconLeaflet,
              },
              circlemarker: false,
            }}
          />
        </FeatureGroup>
      </MapContainer>
    ),
    [results, customSearch],
  );

  useEffect(() => {
    analyticsService.send();
  }, []);

  return (
    <Box
      style={{
        marginTop: headerHeight,
        height: `calc(100vh - ${headerHeight}px)`,
        overflowX: "hidden",
      }}
    >
      {displayMap}
      {map && (
        <ExplorerModal
          setPointsAndBoundaries={setPointAndBoundaries}
          pointsAndBoundaries={results}
          map={map}
          handleCustomSearch={handleCustomSearch}
          handleSearchFeatures={setSearchFeatures}
          searchFeatures={searchFeatures}
          customSearch={customSearch}
          parentDrawControl={drawControl}
          radiusRef={radiusRef}
        />
      )}
    </Box>
  );
};

export default Explorer;
